import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { LISTING_STATE_DRAFT } from '../../util/types';
import { ensureListing, getListingFieldsFromURL, generateShippingWeight } from '../../util/data';
import { EditListingFeaturesForm } from '../../forms';
import { ListingLink, WizardSelections } from '../../components';
import { types as sdkTypes } from '../../util/sdkLoader';
import config from '../../config';

import css from './EditListingFeaturesPanel.module.css';

const FEATURES_NAME = 'amenities';
const { Money } = sdkTypes;

const EditListingFeaturesPanel = props => {
  const {
    rootClassName,
    className,
    listing,
    disabled,
    ready,
    onSubmit,
    onChange,
    submitButtonText,
    panelUpdated,
    updateInProgress,
    errors,
    params,
    intl,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const { publicData } = currentListing.attributes;

  const isPublished = currentListing.id && currentListing.attributes.state !== LISTING_STATE_DRAFT;
  const panelTitle = isPublished ? (
    <FormattedMessage
      id="EditListingFeaturesPanel.title"
      values={{
        listingTitle: <ListingLink listing={listing} />,
      }}
    />
  ) : (
    <FormattedMessage id="EditListingFeaturesPanel.createListingTitle" />
  );

  const retailPrice = publicData?.retailPrice;
  const initialValues = {
    ...publicData,
    retailPrice: retailPrice ? new Money(retailPrice?.amount, retailPrice?.currency) : null,
  };

  const listingFields = getListingFieldsFromURL(config.custom.editListingCategories, params);
  const listingCategory = params.category || publicData?.category;

  return (
    <div className={classes}>
      <WizardSelections params={params} publicData={publicData} isPublished={isPublished} />
      <h1 className={css.title}>{panelTitle}</h1>
      <EditListingFeaturesForm
        className={css.form}
        name={FEATURES_NAME}
        initialValues={initialValues}
        onSubmit={values => {
          const retailPriceMaybe = values.retailPrice
            ? {
                amount: values.retailPrice.amount,
                currency: values.retailPrice.currency,
              }
            : null;

          const updatedValues = {
            publicData: {
              ...values,
              retailPrice: retailPriceMaybe,
              shippingWeight: generateShippingWeight(listingCategory),
            },
          };
          onSubmit(updatedValues);
        }}
        onChange={onChange}
        saveActionMsg={submitButtonText}
        disabled={disabled}
        ready={ready}
        updated={panelUpdated}
        updateInProgress={updateInProgress}
        fetchErrors={errors}
        listingFields={listingFields}
        intl={intl}
      />
    </div>
  );
};

EditListingFeaturesPanel.defaultProps = {
  rootClassName: null,
  className: null,
  listing: null,
};

const { bool, func, object, string } = PropTypes;

EditListingFeaturesPanel.propTypes = {
  rootClassName: string,
  className: string,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,

  disabled: bool.isRequired,
  ready: bool.isRequired,
  onSubmit: func.isRequired,
  onChange: func.isRequired,
  submitButtonText: string.isRequired,
  panelUpdated: bool.isRequired,
  updateInProgress: bool.isRequired,
  errors: object.isRequired,
};

export default EditListingFeaturesPanel;
